// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Calendar, MapPin, ChevronRight, RefreshCw, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import EventDetailScreen from './EventDetailScreen';
import { Input } from '@/components/ui/input';
import { parseLocalDate } from '@/utils/formatters';

interface PublicEventsScreenProps {
  selectedEventId: string | null;
  onSelectEvent: (eventId: string) => void;
  onBack: () => void;
  userEmail?: string;
  profileId?: string;
}

interface PublicEvent {
  id: string;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  venue_city: string | null;
  venue_state: string | null;
  thumbnail_url: string | null;
  status: string;
}

const SUPABASE_STORAGE_URL = 'https://kvttsygfutwcywroraji.supabase.co/storage/v1/object/public/event-assets/';

const PublicEventsScreen: React.FC<PublicEventsScreenProps> = ({ 
  selectedEventId, 
  onSelectEvent,
  onBack,
  userEmail,
  profileId
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: events, isLoading, refetch } = useQuery({
    queryKey: ['public-events-mobile'],
    queryFn: async (): Promise<PublicEvent[]> => {
      console.log('[PublicEvents] Fetching all published events');
      
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('events')
        .select('id, name, description, start_date, end_date, venue_city, venue_state, thumbnail_url, status')
        .eq('status', 'published')
        .eq('is_active', true)
        .eq('is_archived', false)
        .gte('end_date', today)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('[PublicEvents] Error fetching events:', error);
        throw error;
      }

      console.log('[PublicEvents] Fetched events count:', data?.length || 0);
      return data || [];
    },
  });

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);

  // Filter events by search query
  const filteredEvents = events?.filter(event => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      event.name.toLowerCase().includes(query) ||
      event.venue_city?.toLowerCase().includes(query) ||
      event.venue_state?.toLowerCase().includes(query)
    );
  }) || [];

  // Helper to get proper image URL - includes bucket name 'event-featured'
  const getImageUrl = (url: string | null): string | null => {
    if (!url) return null;
    // If it's already a full URL, return it
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Add the bucket name 'event-featured' before the filename
    return `https://kvttsygfutwcywroraji.supabase.co/storage/v1/object/public/event-assets/${url.replace(/^\/+/, '')}`;
  };

  // Show event detail if selected
  if (selectedEventId) {
    return (
      <EventDetailScreen 
        eventId={selectedEventId} 
        onBack={onBack} 
        userId={profileId}
        userEmail={userEmail}
        profileId={profileId}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="sticky top-0 bg-background border-b border-border z-10">
        <div className="flex items-center justify-between px-4 h-14">
          <h1 className="text-lg font-semibold">Events</h1>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw className={cn("h-5 w-5", isRefreshing && "animate-spin")} />
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 bg-muted/50"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredEvents.length > 0 ? (
          <div className="p-4 space-y-3">
            {filteredEvents.map((event) => {
              const imageUrl = getImageUrl(event.thumbnail_url);
              
              return (
                <button
                  key={event.id}
                  onClick={() => onSelectEvent(event.id)}
                  className="w-full bg-card rounded-xl border border-border overflow-hidden hover:shadow-md transition-shadow text-left"
                >
                  {/* Thumbnail */}
                  <div className="w-full h-48 bg-gradient-to-br from-primary/10 to-primary/5 overflow-hidden relative">
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt={event.name}
                        className="absolute inset-0 z-10 w-full h-full object-cover"
                        onError={(e) => {
                          // Hide broken image, show gradient placeholder behind
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    {/* Placeholder icon - always visible behind image */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                      <Calendar className="h-12 w-12 text-primary/30" />
                      <span className="text-xs text-muted-foreground">No Image Available</span>
                    </div>
                  </div>

                  {/* Event Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground line-clamp-2 mb-2">
                      {event.name}
                    </h3>
                    
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mb-1">
                      <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        {format(parseLocalDate(event.start_date), 'MMM d, yyyy')}
                        {event.end_date && event.end_date !== event.start_date && (
                          <> - {format(parseLocalDate(event.end_date), 'MMM d, yyyy')}</>
                        )}
                      </span>
                    </div>
                    
                    {(event.venue_city || event.venue_state) && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                          {[event.venue_city, event.venue_state].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">
              {searchQuery ? 'No events found' : 'No upcoming events'}
            </h3>
            <p className="text-sm text-muted-foreground text-center">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Check back later for new events'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicEventsScreen;
